/**
 * ChatPanel Component
 *
 * Complete chat interface with message list, input field, and thinking indicator.
 * Connects to useAgentChat hook for SSE streaming communication with equip-template agent.
 * Auto-scrolls to bottom on new messages.
 */

import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Send, Trash2, Sparkles } from 'lucide-react'
import { useAgentChat } from '@/hooks/useAgentChat'
import { ChatMessage } from './ChatMessage'
import { cn } from '@/lib/utils'
import type { ThinkingStage } from '@/lib/agent-chat'

interface ChatPanelProps {
  currentHtml: string
  templateName?: string
  onHtmlUpdate?: (html: string) => void
  onAiProcessingChange?: (processing: boolean) => void
}

// Thinking stage display names
const THINKING_STAGE_LABELS: Record<ThinkingStage, string> = {
  understanding: 'Understanding your request...',
  planning: 'Planning changes...',
  querying: 'Querying data...',
  analyzing: 'Analyzing template...',
  generating: 'Generating HTML...',
  visualizing: 'Creating visualization...',
  complete: 'Complete'
}

// Example prompts for welcome message
const EXAMPLE_PROMPTS = [
  'Add a new section for appointment reminders',
  'Make the header text larger and bold',
  'Change the button color to teal',
  'Add a footer with contact information'
]

export function ChatPanel({ currentHtml, templateName, onHtmlUpdate, onAiProcessingChange }: ChatPanelProps) {
  const [inputValue, setInputValue] = useState('')
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const {
    messages,
    isLoading,
    isStreaming,
    isReceivingContent,
    thinkingStage,
    thinkingMessage,
    error,
    sendMessage,
    retryLastMessage,
    clearMessages,
    clearError
  } = useAgentChat({ onHtmlUpdate })

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, thinkingStage])

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Notify parent of AI processing state changes (only lock editor when receiving content)
  useEffect(() => {
    onAiProcessingChange?.(isReceivingContent)
  }, [isReceivingContent, onAiProcessingChange])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!inputValue.trim() || isLoading || isStreaming) return

    const message = inputValue.trim()
    setInputValue('')

    await sendMessage(message, currentHtml, templateName)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (but Shift+Enter for newline)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleClearChat = () => {
    setShowClearConfirm(true)
  }

  const confirmClearChat = () => {
    clearMessages()
    setInputValue('')
    setShowClearConfirm(false)
  }

  const cancelClearChat = () => {
    setShowClearConfirm(false)
  }

  const handleExampleClick = (example: string) => {
    setInputValue(example)
    inputRef.current?.focus()
  }

  const isInputDisabled = isLoading || isStreaming

  return (
    <div className="h-full flex flex-col relative">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-eventus-purple/5 to-eventus-teal/5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-eventus-purple to-eventus-teal/80 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-eventus-navy">AI Assistant</h3>
            <p className="text-xs text-slate-500">Describe changes in natural language</p>
          </div>
        </div>

        {messages.length > 0 && (
          <button
            onClick={handleClearChat}
            className="p-1.5 rounded-md hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
            title="Clear chat"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Clear Chat Confirmation Dialog */}
      <AnimatePresence>
        {showClearConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl p-4 mx-4 max-w-xs"
            >
              <p className="text-sm font-medium text-slate-900 mb-3">Clear all messages?</p>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={cancelClearChat}
                  className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmClearChat}
                  className="px-3 py-1.5 rounded-md text-sm bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  Clear
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {messages.length === 0 ? (
          // Welcome message with examples
          <div className="h-full flex items-center justify-center">
            <div className="max-w-md text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-eventus-purple/10 to-eventus-teal/10 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-eventus-purple" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-eventus-navy mb-1">
                  Welcome to AI Editing
                </h4>
                <p className="text-sm text-slate-600">
                  Tell me what changes you'd like to make to this template.
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Try these examples:
                </p>
                <div className="space-y-2">
                  {EXAMPLE_PROMPTS.map((example, i) => (
                    <button
                      key={i}
                      onClick={() => handleExampleClick(example)}
                      className="block w-full text-left px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-sm text-slate-700 transition-colors border border-slate-200 hover:border-eventus-purple/30"
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Message List */}
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}

            {/* Thinking Indicator */}
            <AnimatePresence>
              {thinkingStage && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="flex gap-2 mb-3"
                >
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center mt-1">
                    <Sparkles className="w-4 h-4 text-eventus-purple animate-pulse" />
                  </div>
                  <div className="max-w-[80%] rounded-lg px-3 py-2 bg-eventus-purple/5 border border-eventus-purple/20">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                          className="w-2 h-2 rounded-full bg-eventus-purple"
                        />
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                          className="w-2 h-2 rounded-full bg-eventus-purple"
                        />
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                          className="w-2 h-2 rounded-full bg-eventus-purple"
                        />
                      </div>
                      <span className="text-sm text-eventus-purple font-medium">
                        {thinkingMessage || THINKING_STAGE_LABELS[thinkingStage]}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Error Display */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="flex-shrink-0 px-4 py-2 bg-red-50 border-t border-red-200"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm text-red-700 flex-1">{error}</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={retryLastMessage}
                  className="px-3 py-1 rounded-md bg-eventus-purple text-white text-xs font-medium hover:bg-eventus-purple/90 transition-colors"
                >
                  Retry
                </button>
                <button
                  onClick={clearError}
                  className="text-red-500 hover:text-red-700 text-xs underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input Area */}
      <div className="flex-shrink-0 border-t border-slate-200 bg-white">
        <form onSubmit={handleSubmit} className="p-3">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value)
                  // Auto-resize on change
                  const target = e.target as HTMLTextAreaElement
                  target.style.height = 'auto'
                  target.style.height = `${Math.min(target.scrollHeight, 120)}px`
                }}
                onKeyDown={handleKeyDown}
                placeholder={isInputDisabled ? "AI is processing..." : "Describe changes..."}
                disabled={isInputDisabled}
                rows={1}
                className={cn(
                  "w-full px-3 py-2 rounded-lg border resize-none overflow-y-auto",
                  "text-sm text-slate-900 placeholder:text-slate-400",
                  "focus:outline-none focus:ring-2 focus:ring-eventus-purple/50",
                  "transition-colors",
                  isInputDisabled
                    ? "bg-slate-50 border-slate-200 cursor-not-allowed opacity-60"
                    : "bg-white border-slate-300 hover:border-slate-400"
                )}
                style={{
                  maxHeight: '120px',
                  minHeight: '38px',
                }}
              />
            </div>
            <button
              type="submit"
              disabled={!inputValue.trim() || isInputDisabled}
              className={cn(
                "flex-shrink-0 px-4 py-2 rounded-lg font-medium transition-all",
                "flex items-center gap-2",
                !inputValue.trim() || isInputDisabled
                  ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                  : "bg-eventus-purple text-white hover:bg-eventus-purple/90 shadow-sm hover:shadow"
              )}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
